// src/components/PDFDownloader.js
import React from "react";
import { Tooltip, Button } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { Logo } from "./PDFLogo";
import { useData } from "../context/DataContext";
import { useCanvas } from "../context/CanvasContext";

const PDFDownloader = () => {
  const { selectedProduct, basketItem, setSnackbar } = useData();
  const { canvas } = useCanvas();

  const wrapText = (text, maxWidth, font, fontSize) => {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const width = font.widthOfTextAtSize(testLine, fontSize);
      if (width <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const convertSvgToPng = (svgBase64, width) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = width / img.width;
        canvas.width = width;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = svgBase64;
    });
  };

  const fetchArrayBuffer = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch ${url}`);
    return await res.arrayBuffer();
  };

  const downloadPDF = async () => {
    if (!canvas || !selectedProduct || !basketItem?.lines?.length) {
      setSnackbar({
        open: true,
        message: "Nothing to export",
        severity: "warning",
      });
      return;
    }

    try {
      const canvasEl = canvas.lowerCanvasEl;
      const imgData = canvasEl.toDataURL("image/png");

      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]);
      const { width, height } = page.getSize();

      // === HEADER & FOOTER BARS ===
      const navy = rgb(0.05, 0.14, 0.27);
      page.drawRectangle({
        x: 0,
        y: height - 36,
        width: 612,
        height: 36,
        color: navy,
      });
      page.drawRectangle({ x: 0, y: 0, width: 612, height: 36, color: navy });

      // === LOGO ===
      let logoHeightPt = 36;
      try {
        const logoWidthPt = 120;
        const logoPngData = await convertSvgToPng(Logo.svg, logoWidthPt * 2);
        const logoPng = await pdfDoc.embedPng(logoPngData);
        logoHeightPt = logoWidthPt * (logoPng.height / logoPng.width);
        page.drawImage(logoPng, {
          x: 18,
          y: height - 36 + (36 - logoHeightPt) / 2,
          width: logoWidthPt,
          height: logoHeightPt,
        });
      } catch (e) {
        console.warn("Logo failed");
      }

      // === PHONE & WEBSITE ===
      const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const topTextSize = 14;
      page.setFont(regularFont);
      page.setFontSize(topTextSize);
      page.setFontColor(rgb(1, 1, 1));

      const phoneText = "800-582-6366";
      const phoneWidth = regularFont.widthOfTextAtSize(phoneText, topTextSize);
      page.drawText(phoneText, {
        x: width - 18 - phoneWidth,
        y: height - 36 + (36 - topTextSize) / 2,
      });

      const siteText = "www.alphabetsigns.com";
      const siteWidth = regularFont.widthOfTextAtSize(siteText, topTextSize);
      page.drawText(siteText, {
        x: width - 18 - siteWidth,
        y: (36 - topTextSize) / 2,
      });

      // === CANVAS IMAGE ===
      const pngImage = await pdfDoc.embedPng(imgData);
      const targetWidthPt = 540;
      const scaleFactor = targetWidthPt / canvas.width;
      const targetHeightPt = canvas.height * scaleFactor;

      page.drawImage(pngImage, {
        x: (width - targetWidthPt) / 2,
        y: height - 36 - targetHeightPt - 18,
        width: targetWidthPt,
        height: targetHeightPt,
      });

      // === TITLE ===
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const title = selectedProduct?.name || "Custom Sign Design";
      page.drawText(title, {
        x: 36,
        y: height - 36 - targetHeightPt - 18 - 30,
        size: 24,
        font: boldFont,
        color: navy,
      });

      // === DESCRIPTION ===
      const description =
        selectedProduct?.description || "No description available";
      const descriptionLines = wrapText(description, 540, regularFont, 12);
      descriptionLines.forEach((line, i) => {
        page.drawText(line, {
          x: 36,
          y: height - 36 - targetHeightPt - 18 - 70 - i * 16,
          size: 12,
          font: regularFont,
          color: rgb(0, 0, 0),
        });
      });

      // === LINE ITEMS & TOTAL ===
      let y =
        height - 36 - targetHeightPt - 18 - 100 - descriptionLines.length * 16;

      basketItem.lines.forEach((line, i) => {
        // Get dimensions for this line
        const w = line.widthInches
          ? Number(line.widthInches).toFixed(2)
          : "?.??";
        const h = line.heightInches
          ? Number(line.heightInches).toFixed(2)
          : "?.??";
        const dimText = `${w}" x ${h}"`;

        const text = `Line ${i + 1}: "${line.phrase}" • ${line.size} inch • ${
          line.colorName
        } • ${line.font}• ${dimText} • $${Number(line.price || 0).toFixed(2)}`;
        page.drawText(text, {
          x: 50,
          y: y - i * 20,
          size: 11,
          font: regularFont,
          color: rgb(0, 0, 0),
        });
      });

      const total = basketItem.lines.reduce(
        (sum, l) => sum + Number(l.price || 0),
        0
      );
      page.drawText(`Total: $${total.toFixed(2)}`, {
        x: width - 200,
        y: y - basketItem.lines.length * 20 - 30,
        size: 18,
        font: boldFont,
        color: navy,
      });

      // === Quote Disclaimer ===
      const disclaimer =
        "Quote valid for 30 days. Final price and availability will be determined upon production review.";
      const disclaimerLines = wrapText(disclaimer, 540, regularFont, 8);
      disclaimerLines.forEach((line, i) => {
        const lineW = regularFont.widthOfTextAtSize(line, 8);
        page.drawText(line, {
          x: width - 18 - lineW,
          y: y - basketItem.lines.length * 20 - 60 - i * 10,
          size: 8,
          font: regularFont,
          color: rgb(0, 0, 0),
        });
      });

      // === MERGE ADDITIONAL PDFs ===
      const pdfUrls = Array.isArray(selectedProduct?.pdf)
        ? selectedProduct.pdf
        : [];
      let mergedCount = 0;

      for (const pdfUrl of pdfUrls) {
        const fullUrl =
          process.env.NODE_ENV === "development"
            ? `/sign_builder/pdfs/${pdfUrl}`
            : `https://www.alphabetsigns.com/sign_builder/pdfs/${pdfUrl}`;

        try {
          const pdfBytes = await fetchArrayBuffer(fullUrl);
          const externalDoc = await PDFDocument.load(pdfBytes);
          const copiedPages = await pdfDoc.copyPages(
            externalDoc,
            externalDoc.getPageIndices()
          );
          copiedPages.forEach((p) => pdfDoc.addPage(p));
          mergedCount++;
        } catch (err) {
          console.error(`Failed to merge ${pdfUrl}`, err);
          setSnackbar({
            open: true,
            message: `Failed to merge ${pdfUrl}`,
            severity: "warning",
          });
        }
      }

      // === DOWNLOAD ===
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedProduct.code || "design"}_quote.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "PDF downloaded!",
        severity: "success",
      });
    } catch (err) {
      console.error("PDF Error:", err);
      setSnackbar({
        open: true,
        message: "Failed to generate PDF",
        severity: "error",
      });
    }
  };

  return (
    <Tooltip title="Download PDF Quote">
      <span>
        <Button
          onClick={downloadPDF}
          variant="contained"
          color="primary"
          startIcon={<PictureAsPdfIcon />}
          sx={{ textTransform: "none" }}
          disabled={!canvas || !basketItem?.lines?.length}
        >
          Quote
        </Button>
      </span>
    </Tooltip>
  );
};

export default PDFDownloader;
